/**
 * ChatMessage 组件
 * 统一的消息气泡组件，支持 AgentFlow 和 MemoryService 两种风格
 */

import { memo, useState } from 'react'
import { Bot, Volume2, Smartphone, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import type { ChatMessage as ChatMessageType, ChatVariant, ChatTheme, AudioFormat } from './types'

interface ChatMessageProps {
  message: ChatMessageType
  isStreaming?: boolean
  variant?: ChatVariant
  theme?: ChatTheme
  // v6.0: 语音播放支持
  isAudioPlaying?: boolean  // 是否正在播放音频（全局状态）
  onPlayAudio?: (audioData: string, format: AudioFormat) => void  // 点击播放按钮回调
}

const formatTime = (timestamp: Date | string) => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export const ChatMessage = memo(({ 
  message, 
  isStreaming: isStreamingProp = false,
  variant = 'agent',
  theme = 'light',
  isAudioPlaying = false,
  onPlayAudio,
}: ChatMessageProps) => {
  const isUser = message.role === 'user'
  // 优先使用消息自身的 isStreaming 属性
  const isStreaming = message.isStreaming ?? isStreamingProp
  // v13.0: 推理过程折叠状态（默认展开）
  const [reasoningExpanded, setReasoningExpanded] = useState(true)
  // 深色主题支持：variant === 'memory' 或 theme === 'dark' 时使用深色样式
  
  // 格式化音频时长
  const formatDuration = (seconds?: number) => {
    if (!seconds) return ''
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  
  // 是否显示播放按钮：有音频数据且不在流式输入中
  const showAudioButton = !isUser && message.audioData && !message.isStreaming
  // 播放按钮是否禁用：全局正在播放时禁用
  const isAudioButtonDisabled = isAudioPlaying

  // AgentFlow 风格（浅色）
  if (variant === 'agent' && theme === 'light') {
    return (
      <div className="space-y-1">
        {isUser ? (
          <div className="flex justify-end">
            <div className="max-w-[85%]">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-3 rounded-2xl rounded-br-md shadow-sm">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
              </div>
              <p className="text-[11px] text-slate-400 mt-1 text-right pr-1">
                {formatTime(message.timestamp)}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            <div className="shrink-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                <Bot className="h-4 w-4 text-white" />
              </div>
            </div>
            <div className="flex-1 max-w-[85%] space-y-2">
              {/* v13.0: 历史消息推理过程（在回复上方，默认展开，可折叠） */}
              {message.reasoning && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setReasoningExpanded(v => !v)}
                    className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Sparkles className="h-3 w-3 text-slate-400" />
                    <span className="font-medium flex-1 text-left">推理过程</span>
                    {reasoningExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                  {reasoningExpanded && (
                    <div className="px-3 pb-3">
                      <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">{message.reasoning}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-md shadow-sm border border-slate-100">
                <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                  {message.content}
                  {isStreaming && (
                    <span className="inline-block w-[2px] h-[14px] bg-violet-500 animate-pulse ml-[2px] align-middle" />
                  )}
                </p>
                {/* v6.0: 语音播放按钮 */}
                {showAudioButton && (
                  <div className="flex items-center justify-end mt-2 pt-2 border-t border-slate-100">
                    <button
                      onClick={() => onPlayAudio?.(message.audioData!, message.audioFormat || 'mp3')}
                      disabled={isAudioButtonDisabled}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-colors ${
                        isAudioButtonDisabled
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-violet-50 text-violet-600 hover:bg-violet-100'
                      }`}
                    >
                      <Volume2 className="h-3.5 w-3.5" />
                      <span>{formatDuration(message.audioDuration)}</span>
                    </button>
                  </div>
                )}
                {/* v8.1: 设备指令气泡（Admin v2 Webhook 模式） */}
                {message.deviceCommand && (
                  <div className="mt-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                          <Smartphone className="h-3 w-3 text-blue-600" />
                        </div>
                        <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                          设备指令
                        </span>
                      </div>
                      <div className="bg-white rounded border border-slate-100 p-2 font-mono text-sm text-slate-800">
                        <pre className="whitespace-pre-wrap">
                          {JSON.stringify(message.deviceCommand, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-slate-400 pl-1">
                {formatTime(message.timestamp)}
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // MemoryService 风格（深色）
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-slide-up`}>
      <div
        className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
          isUser 
            ? 'rounded-br-md bg-blue-500' 
            : 'rounded-bl-md bg-secondary border border-border'
        }`}
      >
        <p className={`text-sm whitespace-pre-wrap leading-relaxed ${
          isUser ? 'text-white' : 'text-foreground'
        }`}>
          {message.content}
          {isStreaming && (
            <span className="inline-block w-2 h-4 ml-1 animate-pulse bg-white/70" />
          )}
        </p>
        <div className={`text-[10px] mt-1 ${
          isUser ? 'text-right text-white/70' : 'text-left text-muted-foreground'
        }`}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  )
})

ChatMessage.displayName = 'ChatMessage'

